import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReplyIcon from '@mui/icons-material/Reply';
import SendIcon from '@mui/icons-material/Send';

const IELTS_CHAT_REST_URL = 'https://bzjlbetbpkkwfgydrgcf.supabase.co/rest/v1';
const IELTS_CHAT_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJ6amxiZXRicGtrd2ZneWRyZ2NmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzg2ODIwMzQsImV4cCI6MjA5NDI1ODAzNH0.oaDHSOgEIgPj1fIjpRT7NA7AwxpNqBL283ymEUNxN0g';
const IELTS_CHAT_ROOM_SLUG = 'ielts-community';
const IELTS_CHAT_MESSAGE_LIMIT = 1000;
const IELTS_CHAT_DELETE_WINDOW_MS = 10 * 60 * 1000;

const ieltsChatHeaders = {
  apikey: IELTS_CHAT_ANON_KEY,
  Authorization: `Bearer ${IELTS_CHAT_ANON_KEY}`,
  'Content-Type': 'application/json',
};

async function getSupabaseErrorMessage(response, fallbackMessage) {
  let detail = '';
  try {
    const data = await response.clone().json();
    detail = data?.message || data?.hint || '';
  } catch (error) {
    detail = '';
  }

  if (response.status === 401 || response.status === 403) return 'Community is temporarily unavailable. Please try again shortly.';
  return detail || fallbackMessage;
}

function getSwiftUniUserId(userData) {
  return String(userData?.UserId || userData?.id || userData?.user_id || userData?.email || userData?.Email || '').trim();
}

function getSwiftUniUserName(userData) {
  const nameCandidate =
    userData?.Name ||
    userData?.name ||
    userData?.FullName ||
    userData?.fullName ||
    userData?.full_name ||
    userData?.displayName ||
    userData?.display_name ||
    userData?.FirstName ||
    userData?.firstName ||
    userData?.first_name ||
    userData?.LastName ||
    userData?.lastName ||
    userData?.last_name ||
    userData?.user?.name ||
    userData?.user?.full_name ||
    userData?.user?.display_name ||
    userData?.UserName ||
    userData?.username ||
    userData?.user_name;
  const name = String(nameCandidate || '').trim();
  if (name && !name.includes('@')) return name;
  return 'Student';
}

function getSwiftUniUserEmail(userData) {
  return String(userData?.Email || userData?.email || '').trim();
}

function toDisplayName(value = '') {
  const text = String(value || '').trim();
  if (!text) return '';
  if (!text.includes('@')) return text;
  const localPart = text.split('@')[0] || '';
  const normalized = localPart
    .replace(/[._-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!normalized) return '';
  return normalized
    .split(' ')
    .map((word) => (word ? `${word.charAt(0).toUpperCase()}${word.slice(1)}` : ''))
    .join(' ')
    .trim();
}

function getMessageDisplayName(message) {
  const directName = toDisplayName(message?.user_name);
  if (directName) return directName;
  const emailBasedName = toDisplayName(message?.user_email);
  if (emailBasedName) return emailBasedName;
  return 'Student';
}

function formatChatTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function canDeleteChatMessage(message, currentUserId) {
  if (!message?.created_at || !currentUserId) return false;
  const createdTime = new Date(message.created_at).getTime();
  if (Number.isNaN(createdTime)) return false;
  return String(message.swiftuni_user_id) === String(currentUserId) && Date.now() - createdTime <= IELTS_CHAT_DELETE_WINDOW_MS;
}

const CommunityChat = ({ userData = {}, compact = false }) => {
  const [chatRoom, setChatRoom] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSending, setChatSending] = useState(false);
  const [chatError, setChatError] = useState('');
  const [chatText, setChatText] = useState('');
  const [replyToMessage, setReplyToMessage] = useState(null);
  const chatScrollRef = useRef(null);

  const chatUserId = getSwiftUniUserId(userData);
  const chatUserName = getSwiftUniUserName(userData);
  const chatUserEmail = getSwiftUniUserEmail(userData);

  const chatMessageMap = useMemo(() => {
    const map = new Map();
    chatMessages.forEach((message) => map.set(message.id, message));
    return map;
  }, [chatMessages]);

  const fetchCommunityMessages = useCallback(async (roomId = chatRoom?.id, options = {}) => {
    if (!roomId) return;
    if (!options.silent) setChatLoading(true);
    setChatError('');
    try {
      const response = await fetch(
        `${IELTS_CHAT_REST_URL}/ielts_chat_messages?room_id=eq.${roomId}&deleted_at=is.null&select=id,room_id,swiftuni_user_id,user_name,user_email,message_text,reply_to_message_id,created_at&order=created_at.desc&limit=100`,
        { headers: ieltsChatHeaders }
      );
      if (!response.ok) throw new Error(await getSupabaseErrorMessage(response, 'Unable to load community messages'));
      const data = await response.json();
      setChatMessages(Array.isArray(data) ? data.reverse() : []);
    } catch (error) {
      setChatError(error.message || 'Unable to load community messages');
    } finally {
      if (!options.silent) setChatLoading(false);
    }
  }, [chatRoom?.id]);

  const loadCommunityRoom = useCallback(async () => {
    setChatLoading(true);
    setChatError('');
    try {
      const response = await fetch(
        `${IELTS_CHAT_REST_URL}/ielts_chat_rooms?slug=eq.${IELTS_CHAT_ROOM_SLUG}&select=id,name&limit=1`,
        { headers: ieltsChatHeaders }
      );
      if (!response.ok) throw new Error(await getSupabaseErrorMessage(response, 'Unable to open IELTS community'));
      const data = await response.json();
      const room = Array.isArray(data) ? data[0] : null;
      if (!room?.id) throw new Error('IELTS community room was not found');
      setChatRoom(room);
      await fetchCommunityMessages(room.id, { silent: true });
    } catch (error) {
      setChatError(error.message || 'Unable to open IELTS community');
    } finally {
      setChatLoading(false);
    }
  }, [fetchCommunityMessages]);

  useEffect(() => {
    if (!chatRoom?.id) {
      loadCommunityRoom();
      return undefined;
    }

    fetchCommunityMessages(chatRoom.id, { silent: true });
    const intervalId = setInterval(() => {
      fetchCommunityMessages(chatRoom.id, { silent: true });
    }, 8000);

    return () => clearInterval(intervalId);
  }, [chatRoom?.id, fetchCommunityMessages, loadCommunityRoom]);

  useEffect(() => {
    if (!chatScrollRef.current) return;
    chatScrollRef.current.scrollTo({
      top: chatScrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [chatMessages.length]);

  const handleSendCommunityMessage = async () => {
    const trimmedMessage = chatText.trim();
    if (!chatRoom?.id || !chatUserId || !trimmedMessage || trimmedMessage.length > IELTS_CHAT_MESSAGE_LIMIT) return;

    setChatSending(true);
    setChatError('');
    try {
      const response = await fetch(`${IELTS_CHAT_REST_URL}/ielts_chat_messages`, {
        method: 'POST',
        headers: {
          ...ieltsChatHeaders,
          Prefer: 'return=minimal',
        },
        body: JSON.stringify({
          room_id: chatRoom.id,
          swiftuni_user_id: chatUserId,
          user_name: chatUserName,
          user_email: chatUserEmail || null,
          message_text: trimmedMessage,
          reply_to_message_id: replyToMessage?.id || null,
        }),
      });
      if (!response.ok) throw new Error(await getSupabaseErrorMessage(response, 'Message was not sent. Please try again.'));
      setChatText('');
      setReplyToMessage(null);
      await fetchCommunityMessages(chatRoom.id, { silent: true });
    } catch (error) {
      setChatError(error.message || 'Message was not sent. Please try again.');
    } finally {
      setChatSending(false);
    }
  };

  const handleDeleteCommunityMessage = async (message) => {
    if (!canDeleteChatMessage(message, chatUserId)) return;

    setChatError('');
    try {
      const response = await fetch(
        `${IELTS_CHAT_REST_URL}/ielts_chat_messages?id=eq.${message.id}&swiftuni_user_id=eq.${encodeURIComponent(chatUserId)}`,
        {
          method: 'PATCH',
          headers: {
            ...ieltsChatHeaders,
            Prefer: 'return=minimal',
          },
          body: JSON.stringify({ deleted_at: new Date().toISOString() }),
        }
      );
      if (!response.ok) throw new Error(await getSupabaseErrorMessage(response, 'Message could not be deleted'));
      await fetchCommunityMessages(chatRoom?.id, { silent: true });
    } catch (error) {
      setChatError(error.message || 'Message could not be deleted');
    }
  };

  return (
    <>
      <Box sx={{ mb: compact ? 1 : 2, flexShrink: 0 }}>
        <Typography variant={compact ? 'subtitle1' : 'h5'} sx={{ fontWeight: 900, color: '#211936' }}>
          IELTS Community
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3, fontSize: compact ? 12.5 : undefined }}>
          Ask questions, share progress, and help each other prepare.
        </Typography>
      </Box>

      <Paper
        sx={{
          borderRadius: '16px',
          border: '1px solid rgba(153, 108, 254, 0.14)',
          overflow: 'hidden',
          background: '#fff',
          boxShadow: '0 10px 24px rgba(41, 22, 89, 0.06)',
          display: 'flex',
          flexDirection: 'column',
          height: compact
            ? '100%'
            : {
              xs: 'calc(100dvh - 250px)',
              sm: 'calc(100dvh - 260px)',
              md: 'calc(100dvh - 280px)',
            },
          minHeight: compact ? 0 : { xs: 430, md: 500 },
          maxHeight: compact ? 'none' : 680,
          minWidth: 0,
        }}
      >
        <Box
          ref={chatScrollRef}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            p: compact ? 1 : { xs: 1.4, sm: 2 },
            background: 'linear-gradient(180deg, #fbf9ff 0%, #ffffff 100%)',
          }}
        >
          {chatLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100%">
              <CircularProgress size={28} />
            </Box>
          ) : chatMessages.length === 0 ? (
            <Box
              sx={{
                minHeight: '100%',
                display: 'grid',
                placeItems: 'center',
                textAlign: 'center',
                px: 2,
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 900, color: '#231b3d', mb: 0.6 }}>
                  No messages yet
                </Typography>
                <Typography variant="body2" sx={{ color: '#6f6680' }}>
                  Start the IELTS community conversation with a question or study update.
                </Typography>
              </Box>
            </Box>
          ) : (
            <Stack spacing={compact ? 1 : 0.8}>
              {chatMessages.map((message) => {
                const isOwnMessage = String(message.swiftuni_user_id) === String(chatUserId);
                const originalMessage = message.reply_to_message_id ? chatMessageMap.get(message.reply_to_message_id) : null;
                const deleteAllowed = canDeleteChatMessage(message, chatUserId);
                return (
                  <Box
                    key={message.id}
                    sx={{
                      display: 'flex',
                      justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
                      px: compact ? 0.2 : 0,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: isOwnMessage ? 'row-reverse' : 'row',
                        alignItems: 'flex-end',
                        gap: 0.45,
                        maxWidth: compact ? '100%' : '92%',
                      }}
                    >
                      <Paper
                        sx={{
                          width: 'fit-content',
                          maxWidth: compact ? '82%' : { xs: '92%', sm: 'min(68%, 540px)' },
                          p: compact ? '6px 9px' : 1,
                          borderRadius: isOwnMessage ? '13px 13px 4px 13px' : '13px 13px 13px 4px',
                          border: 'none',
                          background: isOwnMessage ? '#DCF8C6' : '#fff',
                          boxShadow: '0 5px 12px rgba(41, 22, 89, 0.06)',
                        }}
                      >
                      {originalMessage && (
                        <Box
                          sx={{
                            mb: 0.5,
                            p: 0.55,
                            borderRadius: '7px',
                            borderLeft: '3px solid #42B883',
                            background: 'rgba(255, 255, 255, 0.55)',
                          }}
                        >
                          <Typography variant="caption" sx={{ display: 'block', fontWeight: 900, color: '#5d42a5' }}>
                            Replying to {getMessageDisplayName(originalMessage)}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#5f576e',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {originalMessage.message_text}
                          </Typography>
                        </Box>
                      )}

                      <Box display="flex" justifyContent="space-between" gap={1} alignItems="center" mb={0.25}>
                        <Typography variant="caption" sx={{ fontWeight: 900, color: '#30264d' }}>
                          {getMessageDisplayName(message)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#8a8298', whiteSpace: 'nowrap' }}>
                          {formatChatTime(message.created_at)}
                        </Typography>
                      </Box>
                      <Typography sx={{ color: '#1f2933', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: compact ? 12.8 : 13.6, lineHeight: 1.38 }}>
                        {message.message_text}
                      </Typography>
                      </Paper>
                      <Stack spacing={0.3} sx={{ mb: 0.15 }}>
                        <IconButton
                          size="small"
                          onClick={() => setReplyToMessage(message)}
                          sx={{
                            width: 24,
                            height: 24,
                            border: '1px solid rgba(107, 77, 214, 0.22)',
                            color: '#6b4dd6',
                            background: '#fff',
                            '&:hover': { background: '#f5f0ff' },
                          }}
                        >
                          <ReplyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                        {deleteAllowed && (
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteCommunityMessage(message)}
                            sx={{
                              width: 24,
                              height: 24,
                              border: '1px solid rgba(180, 35, 24, 0.22)',
                              color: '#b42318',
                              background: '#fff',
                              '&:hover': { background: '#fff0f0' },
                            }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        )}
                      </Stack>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>

        <Box sx={{ flexShrink: 0, p: compact ? 1 : { xs: 1.1, sm: 1.4 }, borderTop: '1px solid #edf0f7', background: '#fff' }}>
          {chatError && (
            <Alert severity="error" sx={{ mb: 1.2, borderRadius: '10px' }}>
              {chatError}
            </Alert>
          )}
          {replyToMessage && (
            <Box
              sx={{
                mb: 1,
                p: 1,
                borderRadius: '10px',
                background: '#f6f1ff',
                border: '1px solid rgba(153, 108, 254, 0.18)',
                display: 'flex',
                justifyContent: 'space-between',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={{ fontWeight: 900, color: '#5d42a5' }}>
                  Replying to {getMessageDisplayName(replyToMessage)}
                </Typography>
                <Typography variant="caption" sx={{ color: '#5f576e', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {replyToMessage.message_text}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setReplyToMessage(null)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                component="textarea"
                value={chatText}
                disabled={!chatUserId || chatSending}
                onChange={(event) => setChatText(event.target.value.slice(0, IELTS_CHAT_MESSAGE_LIMIT))}
                placeholder={chatUserId ? 'Write a message...' : 'Please login to join the community'}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    handleSendCommunityMessage();
                  }
                }}
                sx={{
                  width: '100%',
                  height: compact ? 46 : 76,
                  boxSizing: 'border-box',
                  resize: 'none',
                  border: '1px solid rgba(0, 0, 0, 0.23)',
                  borderRadius: '14px',
                  background: '#fbfbff',
                  color: '#1f2933',
                  font: 'inherit',
                  fontSize: 14,
                  lineHeight: 1.35,
                  outline: 'none',
                  p: '10px 12px',
                  transition: 'border-color 160ms ease, box-shadow 160ms ease',
                  '&::placeholder': {
                    color: '#8b8498',
                    opacity: 1,
                  },
                  '&:hover:not(:disabled)': {
                    borderColor: 'rgba(0, 0, 0, 0.52)',
                  },
                  '&:focus': {
                    borderColor: '#7351e9',
                    boxShadow: '0 0 0 3px rgba(153, 108, 254, 0.12)',
                  },
                  '&:disabled': {
                    color: '#9a93a8',
                    background: '#f3f1f7',
                    cursor: 'not-allowed',
                  },
                }}
              />
              <Typography variant="caption" sx={{ display: 'block', mt: 0.3, textAlign: 'right', color: '#7e748f' }}>
                {chatText.length}/{IELTS_CHAT_MESSAGE_LIMIT}
              </Typography>
            </Box>
            <Button
              variant="contained"
              endIcon={<SendIcon />}
              disabled={!chatUserId || chatSending || chatText.trim().length === 0 || chatText.trim().length > IELTS_CHAT_MESSAGE_LIMIT}
              onClick={handleSendCommunityMessage}
              sx={{
                minWidth: { xs: '100%', sm: 120 },
                alignSelf: { xs: 'stretch', sm: 'flex-start' },
                py: compact ? 1.1 : 1.35,
                borderRadius: '14px',
                textTransform: 'none',
                fontWeight: 900,
                background: 'linear-gradient(135deg, #996CFE 0%, #7351e9 100%)',
                color: '#fff',
                '&.Mui-disabled': {
                  background: '#E8E3F4',
                  color: '#7E748F',
                },
                '&.Mui-disabled .MuiButton-endIcon': {
                  color: '#7E748F',
                },
              }}
            >
              Send
            </Button>
          </Stack>
        </Box>
      </Paper>
    </>
  );
};

export default CommunityChat;
